import { XCircle, FileText } from 'lucide-react'
import { Button } from '@/components/ui/button'

interface DeliveryDetailsModalProps {
  partner: any
  onClose: () => void
  onAction: (action: 'approve' | 'reject' | 'block' | 'unblock') => void
}

export function DeliveryDetailsModal({ partner, onClose, onAction }: DeliveryDetailsModalProps) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="p-4 sm:p-6">
          <div className="flex justify-between items-start">
            <h3 className="text-xl font-bold text-gray-900">
              Détails de la Demande Livreur
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <XCircle className="h-6 w-6" />
            </button>
          </div>

          <div className="mt-6 space-y-6">
            {/* Informations Personnelles */}
            <div>
              <h4 className="text-lg font-medium text-gray-900 mb-4">
                Informations Personnelles
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-500">Nom Complet</label>
                  <p className="mt-1 text-sm text-gray-900">{partner.applicant_name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Email</label>
                  <p className="mt-1 text-sm text-gray-900">{partner.applicant_email}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Téléphone</label>
                  <p className="mt-1 text-sm text-gray-900">{partner.applicant_phone}</p>
                </div>
              </div>
            </div>

            {/* Informations de Livraison */}
            <div>
              <h4 className="text-lg font-medium text-gray-900 mb-4">
                Informations de Livraison
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-500">Type de Véhicule</label>
                  <p className="mt-1 text-sm text-gray-900 capitalize">
                    {partner.vehicle_type === 'bike' ? 'Vélo' :
                     partner.vehicle_type === 'motorcycle' ? 'Moto' :
                     partner.vehicle_type === 'car' ? 'Voiture' :
                     partner.vehicle_type === 'van' ? 'Camionnette' : 'À pied'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Zone de Livraison</label>
                  <p className="mt-1 text-sm text-gray-900">{partner.delivery_zone}</p>
                </div>
              </div>
            </div>

            {/* Disponibilités */}
            <div>
              <h4 className="text-lg font-medium text-gray-900 mb-4">
                Disponibilités
              </h4>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {partner.availability && Object.entries(partner.availability).map(([day, available]) => (
                  <div
                    key={day}
                    className={`p-4 rounded-lg border ${
                      available ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                    }`}
                  >
                    <p className="text-sm font-medium capitalize">
                      {day}
                    </p>
                    <p className={`text-xs ${
                      available ? 'text-green-700' : 'text-gray-500'
                    }`}>
                      {available ? 'Disponible' : 'Indisponible'}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Documents */}
            <div>
              <h4 className="text-lg font-medium text-gray-900 mb-4">
                Documents Fournis
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {partner.document_urls && Object.entries(partner.document_urls).map(([key, url]) => (
                  url && (
                    <div key={key} className="relative">
                      <label className="block text-sm font-medium text-gray-500 mb-2 capitalize">
                        {key.replace(/_/g, ' ')}
                      </label>
                      <img
                        src={url as string}
                        alt={key}
                        className="w-full h-48 object-cover rounded-lg border border-gray-200"
                      />
                      <a
                        href={url as string}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="absolute top-8 right-2 p-2 bg-white rounded-full shadow-lg hover:bg-gray-50"
                      >
                        <FileText className="h-4 w-4 text-gray-500" />
                      </a>
                    </div>
                  )
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="pt-6 border-t flex justify-end space-x-3">
              <Button
                variant="secondary"
                onClick={onClose}
              >
                Fermer
              </Button>
              {partner.status === 'pending' && (
                <>
                  <Button
                    variant="outline"
                    className="text-red-600 hover:text-red-700"
                    onClick={() => onAction('reject')}
                  >
                    Rejeter
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => onAction('approve')}
                  >
                    Approuver
                  </Button>
                </>
              )}
              {partner.status === 'active' && (
                <Button
                  variant="outline"
                  className="text-red-600 hover:text-red-700"
                  onClick={() => onAction('block')}
                >
                  Bloquer
                </Button>
              )}
              {partner.status === 'blocked' && (
                <Button
                  variant="primary"
                  onClick={() => onAction('unblock')}
                >
                  Débloquer
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}